/**
 * Enterprise Workforce Suite — add-on card on the Subscription tab.
 *
 * Renders one of three states:
 *   1. Not configured — platform hasn't set a price yet. Shows a
 *      contact-your-admin message.
 *   2. Available for purchase — shows monthly/annual pricing (with the
 *      annual auto-computed) and a "Buy" button that kicks off Razorpay
 *      checkout via the existing gateway.
 *   3. Active — badge + expiry + source (paid / super-admin manual).
 *
 * On successful payment, we call ``refresh()`` from AddonsContext so the
 * nav section appears immediately without a page reload.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAddons } from "@/contexts/AddonsContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, CheckCircle, Sparkle, Info } from "@phosphor-icons/react";

const SUITE_LABEL = "TherapyStack Pro – Enterprise Workforce Management and Payroll Suite";


// Load the Razorpay JS SDK on demand (same pattern as invoice payments).
async function ensureRazorpay() {
  if (window.Razorpay) return true;
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}


export default function EnterpriseWorkforceAddonCard() {
  const { refresh: refreshAddons } = useAddons();
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyingCycle, setBuyingCycle] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/addons/enterprise-workforce");
      setRow(data);
    } catch (e) {
      // Non-admin gets 403 — silently skip. The card is admin-only anyway.
      if (e?.response?.status !== 403) toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const buy = async (cycle) => {
    setBuyingCycle(cycle);
    try {
      const okSdk = await ensureRazorpay();
      if (!okSdk) return toast.error("Razorpay could not load. Check your connection and try again.");
      const { data: order } = await api.post("/addons/enterprise-workforce/checkout", { billing_cycle: cycle });
      const rzp = new window.Razorpay({
        key: order.key_id,
        subscription_id: order.subscription_id,
        name: "TherapyStack Pro",
        description: `Enterprise Workforce Suite (${cycle})`,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#4A6753" },
        handler: async (resp) => {
          try {
            await api.post("/addons/enterprise-workforce/verify", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Enterprise Workforce Suite activated!");
            await load();
            await refreshAddons();
          } catch (e) { toast.error(formatApiError(e)); }
        },
        modal: {
          ondismiss: () => setBuyingCycle(null),
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setBuyingCycle(null);
    }
  };

  if (loading) return null;
  if (!row) return null;

  const active = row.state?.status === "active";
  const monthly = row.pricing?.monthly_price || 0;
  const annual = row.pricing?.annual_price || 0;
  const discount = row.pricing?.annual_discount_pct || 0;
  const notPriced = !active && monthly <= 0;

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="ews-addon-card">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/10 p-2 mt-0.5"><Briefcase size={20} className="text-primary" weight="duotone" /></div>
          <div className="flex-1">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              <span>{SUITE_LABEL}</span>
              {active && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1" data-testid="ews-active-badge">
                  <CheckCircle size={11} weight="fill" /> Active
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Employee Master, workforce scheduling, staff attendance &amp; leave, productivity engine,
              professional payslips, and self-service portal.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {active && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-1" data-testid="ews-active-info">
            <div className="flex items-center gap-2 font-medium">
              <Sparkle size={14} weight="fill" /> This workspace has the Enterprise Suite active.
            </div>
            <div className="text-[11px] opacity-80">
              Billing cycle: {row.state.billing_cycle} · ₹{row.state.active_price?.toLocaleString("en-IN")} ·
              {row.state.source === "super_admin_manual"
                ? " granted by super admin"
                : ` next renewal ${(row.state.expires_at || "").slice(0, 10)}`}
            </div>
          </div>
        )}

        {!active && notPriced && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="ews-not-priced">
            <Info size={14} className="mt-0.5" />
            <div>
              This add-on is not yet available for purchase. The platform admin
              still needs to set a price. Ask your super admin to configure the
              Enterprise Workforce Suite pricing in the Platform admin panel.
            </div>
          </div>
        )}

        {!active && !notPriced && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PriceOption
              label="Monthly"
              price={monthly}
              period="/month"
              onBuy={() => buy("monthly")}
              busy={buyingCycle === "monthly"}
              testId="buy-monthly"
            />
            <PriceOption
              label={`Annual · save ${discount}%`}
              price={annual}
              period="/year"
              highlight
              subtext={`Effective ₹${(annual / 12).toFixed(2)}/mo`}
              onBuy={() => buy("annual")}
              busy={buyingCycle === "annual"}
              testId="buy-annual"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function PriceOption({ label, price, period, highlight, subtext, onBuy, busy, testId }) {
  return (
    <div
      className={`rounded-md border p-4 space-y-3 ${highlight ? "border-primary bg-background" : "bg-background"}`}
      data-testid={`${testId}-card`}
    >
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        {highlight && <Badge className="bg-primary text-primary-foreground border-0">Best value</Badge>}
      </div>
      <div className="flex items-baseline gap-1">
        <div className="text-3xl font-display font-bold">₹{price.toLocaleString("en-IN")}</div>
        <div className="text-xs text-muted-foreground">{period}</div>
      </div>
      {subtext && <div className="text-[11px] text-muted-foreground">{subtext}</div>}
      <Button className="w-full" onClick={onBuy} disabled={busy} data-testid={testId}>
        {busy ? "Opening checkout…" : "Buy now"}
      </Button>
    </div>
  );
}
